<template>
  <OverlayPanel v-if="showingPopover" ref="popover"
    :pt="popoverPassthrough"
    :dismissable="isDismissable"
    @hide="onOverlayPanelHidden()"
  >
    <slot></slot>
  </OverlayPanel>
</template>

<script>
let idCounter = 0;
</script>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from "vue";
import { promiseTimeout } from "@vueuse/core";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";

let props = defineProps([
  "showingPopover",
  "popoverAnchor",
  "popoverPadding",
  "popoverContainerStyle",
  "clickoutIgnoreElems"
]);

let showingPopover = defineModel("showingPopover");

let popover = ref(null);
let popoverRootId = `popoverRoot-${idCounter++}`;
let popoverPassthrough = computed(() => ({
  root: { id: popoverRootId, style: props.popoverContainerStyle },
  content: { style: {
    padding: props.popoverPadding
  }}
}));

watch(showingPopover, async () => {
  let anchorElem = props.popoverAnchor;
  let event = { currentTarget: anchorElem };
  if (showingPopover.value) {
    await nextTick(); // Wait for 'popover.value' to be assigned.
    popover.value.show(event, anchorElem);
    await reposition();

    // Wait for the triggering click event (if any) to complete before adding the clickout handler.
    // The 100ms delay is used in case the triggering event was a mouseup/mousedown instead of click.
    // In that case the event sequence is mousedown -> mouseup -> click, where the click event may not
    // occur for several tens of milliseconds after the triggering mouse event.
    await promiseTimeout(100);
    document.addEventListener("click", clickoutHandler);
  }
  else {
    popover.value.hide();
    document.removeEventListener("click", clickoutHandler);
  }
});

watch(() => props.popoverAnchor, () => {
  if (showingPopover.value)
    reposition();
});

onUnmounted(() => {
  showingPopover.value = false;
  document.removeEventListener("click", clickoutHandler);
});

async function reposition() {
  await promiseTimeout(); // Wait for the DOM to update.
  let popoverRootElem = document.getElementById(popoverRootId);
  let computePosition_opts = { middleware: [
    flip(),     // Flip top/bottom positioning as required.  
    offset(1),  // Offset the popover from the anchor some distance.
    shift({     // Keep the popover edges a minimum distance from the viewport edge.
      padding: 10,
      crossAxis: true
    })
  ]};
  let { x, y } = await computePosition(props.popoverAnchor, popoverRootElem, computePosition_opts);
  popoverRootElem.style.left = `${x}px`;
  popoverRootElem.style.top = `${y}px`;
}

function onOverlayPanelHidden() {
  showingPopover.value = false;
}

// Click-out handling. Close the popover if the clicked element zIndex is less than the popover container zIndex.
// Disabling 'dissmissable' and manually handling click-outs is a workaround to a bug in Primevue v3.52, in which
// clicking inside the content of a nested popover causes all the popovers to close, if the clicked popover is 
// nested three or more levels deep.
const isDismissable = false;
function clickoutHandler(clickEvent) {
  if (didClickoutIgnoredElement(clickEvent)) return;
  let popoverElem = document.getElementById(popoverRootId);
  let popover_zIndex = getCummulativeZIndex(popoverElem);
  let clickedElement_zIndex = getCummulativeZIndex(clickEvent.target);
  if (clickedElement_zIndex < popover_zIndex) {
    showingPopover.value = false;
  }
}

function didClickoutIgnoredElement(clickEvent) {
  let elems = props.clickoutIgnoreElems ?? [];
  return elems.some(elem => elem.contains(clickEvent.target));
}

function getCummulativeZIndex(element) {
  let cummulativeZIndex = 0;
  while (element && element !== document.body) {
    const style = window.getComputedStyle(element);
    const zIndex = style.getPropertyValue('z-index');
    const doesCreateStackingContext = (
      parseFloat(style.opacity) < 1 ||
      style.transform !== 'none' ||
      style.perspective !== 'none' ||
      style.filter !== 'none' ||
      style.mixBlendMode !== 'normal' ||
      style.isolation === 'isolate' ||
      style.willChange.includes('transform') ||
      style.willChange.includes('opacity') ||
      style.willChange.includes('perspective') ||
      (style.display === 'flex' && zIndex !== 'auto') ||
      (style.position !== 'static' && zIndex !== 'auto')
    );
    if (doesCreateStackingContext) {
      cummulativeZIndex += (zIndex == 'auto') ? 0 : parseInt(zIndex);
    }
    element = element.parentElement;
  }
  return cummulativeZIndex;
}

defineExpose({
  reposition
});
</script>